// src/pages/InvoiceNew.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FileText,
  CalendarDays,
  Building2,
  DollarSign,
  Percent,
  Search,
  Plus,
  Minus,
  Copy,
  ArrowUp,
  ArrowDown,
  Info,
  MapPin,
  ArrowLeft,
} from "lucide-react";
import api from "../api/axios";

// ✅ safe currency utilities (prevents RangeError on partial/invalid codes)
import { formatMoney, safeCurrency } from "../utils/money";

/* ----------------------------- local UI primitives ----------------------------- */
const INPUT_BASE =
  "block w-full h-11 rounded-xl border border-slate-300 bg-white px-3.5 text-[0.95rem] leading-6 text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";
const LABEL_BASE = "text-[13px] font-medium text-slate-700";
const HINT_BASE = "text-[12px] text-slate-500";

function LabeledInput({
  id,
  name,
  label,
  labelRight = null,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
  leftAdornment = null,
  rightAdornment = null,
  inputRef,
  onKeyDown,
  onFocus,
  onClick,
  autoComplete = "off",
}) {
  const hasLeft = !!leftAdornment;
  const hasRight = !!rightAdornment;

  return (
    <div className="space-y-1.5">
      {label ? (
        <div className="flex items-center justify-between">
          <label htmlFor={id || name} className={LABEL_BASE}>
            {label} {required ? <span className="text-rose-500">*</span> : null}
          </label>
          {labelRight ? <div className="ml-3 text-[12px]">{labelRight}</div> : null}
        </div>
      ) : null}

      <div className="relative">
        {hasLeft ? (
          <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-slate-400">
            {leftAdornment}
          </div>
        ) : null}
        <input
          ref={inputRef}
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onClick={onClick}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          autoComplete={autoComplete}
          className={[
            INPUT_BASE,
            hasLeft ? "pl-10" : "",
            hasRight ? "pr-10" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          required={required}
        />
        {hasRight ? (
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-slate-400">
            {rightAdornment}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LabeledCheckbox({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-2xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-slate-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-slate-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

/* --------------------------------- math (server-aligned) --------------------------------- */
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
const clamp = (val, min, max) => Math.min(Math.max(Number(val || 0), min), max);

// 🔧 aligned with InvoiceEdit / QuoteEdit: track lineDiscount + base subtotal
function calcLine(li) {
  const qty = Math.max(0, Number(li.quantity || 0));
  const rate = Math.max(0, Number(li.rate || 0));
  const taxPct = clamp(li.taxPercent, 0, 100);
  const dPct = clamp(li.discountPercent, 0, 100);
  const dAmt = Math.max(0, Number(li.discountAmount || 0));
  const taxIncl = !!li.isTaxInclusive;

  let unitBase = rate;
  if (taxIncl && taxPct > 0) unitBase = rate / (1 + taxPct / 100);

  const afterDiscount =
    dAmt > 0
      ? Math.max(0, unitBase - dAmt)
      : unitBase * (1 - (dPct > 0 ? dPct / 100 : 0));

  // ✅ base subtotal (before any discount)
  const subtotalBase = round2(qty * unitBase);
  // ✅ subtotal AFTER discount (used for tax)
  const subtotalAfterDiscount = round2(qty * afterDiscount);
  const lineDiscount = round2(Math.max(0, subtotalBase - subtotalAfterDiscount));

  const taxAmount = round2(
    subtotalAfterDiscount * (taxPct > 0 ? taxPct / 100 : 0)
  );
  const total = round2(subtotalAfterDiscount + taxAmount);

  return {
    subtotalBase,
    subtotal: subtotalAfterDiscount,
    taxAmount,
    total,
    lineDiscount,
  };
}

// ✅ Invoice-level discount removed. Totals only respect per-line discounts.
// ✅ Subtotal is now PRE-discount (sum of base line amounts).
function computeTotals(lineItems) {
  let subtotalBase = 0;
  let taxTotal = 0;
  let lineDiscountTotal = 0;
  let totalAmount = 0;

  for (const li of lineItems) {
    const c = calcLine(li);
    subtotalBase += c.subtotalBase;
    taxTotal += c.taxAmount;
    lineDiscountTotal += c.lineDiscount || 0;
    totalAmount += c.total;
  }

  subtotalBase = round2(subtotalBase);
  taxTotal = round2(taxTotal);
  lineDiscountTotal = round2(lineDiscountTotal);
  totalAmount = round2(totalAmount);

  return {
    subtotalBase,
    taxTotal,
    totalAmount,
    lineDiscountTotal,
  };
}

/* --------------------------------- helpers --------------------------------- */
const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (iso, days) => {
  const d = iso ? new Date(iso) : new Date();
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
};

// ✅ safe money formatter
const money = (n, c = "USD") => formatMoney(n, safeCurrency(c));

const DEFAULT_LINE = {
  description: "",
  quantity: 1,
  rate: 0,
  taxPercent: 0,
  discountPercent: 0,
  discountAmount: 0,
  isTaxInclusive: false,
  showDiscount: false, // UI-only
};

const emptyAddress = {
  street: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  raw: "",
};

const joinAddress = (a) =>
  [a.street, a.city, a.state, a.zip, a.country].filter(Boolean).join(", ");

/* --------------------------------- Page --------------------------------- */

export default function InvoiceNew() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const preselectedClientId = state?.clientId || "";

  // Clients dropdown (portal)
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(preselectedClientId);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOpen, setClientOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputAnchorRef = useRef(null);
  const portalRef = useRef(null);
  const [portalBox, setPortalBox] = useState({ top: 0, left: 0, width: 0 });

  // Basics
  const [currency, setCurrency] = useState("USD"); // will be overridden by company default
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(plusDaysISO(todayISO(), 30));
  const [notes, setNotes] = useState("");

  // Lines
  const [lineItems, setLineItems] = useState([{ ...DEFAULT_LINE }]);
  const [defaultTaxPct, setDefaultTaxPct] = useState(0);
  const [defaultTaxInclusive, setDefaultTaxInclusive] = useState(false);

  // Addresses
  const [keepBillingSynced, setKeepBillingSynced] = useState(true);
  const [useBillingAsShipping, setUseBillingAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState({ ...emptyAddress });
  const [shippingAddress, setShippingAddress] = useState({ ...emptyAddress });

  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => computeTotals(lineItems), [lineItems]);

  // 🔢 aggregated discount = line-level only (invoice-level removed)
  const aggregatedDiscountDisplay = totals.lineDiscountTotal || 0;

  const invalidDueDate =
    issueDate && dueDate ? new Date(dueDate) < new Date(issueDate) : false;

  const canSave =
    selectedClientId &&
    lineItems.length > 0 &&
    lineItems.some(
      (li) =>
        Number(li.quantity) > 0 &&
        (Number(li.rate) > 0 || Number(li.discountAmount) > 0)
    ) &&
    !invalidDueDate;

  /* ---------------------------- data & effects ---------------------------- */
  const parseList = (payload) =>
    Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload)
      ? payload
      : [];

  // ✅ Load company profile once → default currency from company settings
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/company/profile");
        const data = res?.data || {};
        const companyCurrency =
          data.defaultCurrency ||
          data.currency ||
          data.billingCurrency ||
          null;
        if (alive && companyCurrency) {
          setCurrency(String(companyCurrency).toUpperCase());
        }
      } catch (err) {
        console.error("Failed to load company profile for currency", err);
        // swallow; keep default "USD"
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Debounce user query for smoother fetch + UI
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(clientQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [clientQuery]);

  const loadClients = useCallback(
    async (q = "") => {
      try {
        const params = q ? { search: q } : {};
        const res = await api.get("/clients", { params });
        const list = parseList(res.data);
        setClients(list);
        setHighlightIdx(0);

        if (selectedClientId) {
          const found = list.find(
            (c) => String(c._id) === String(selectedClientId)
          );
          if (found) setSelectedClient(found);
        }
      } catch {
        toast.error("Failed to load clients");
      }
    },
    [selectedClientId]
  );

  // initial load
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // fetch on debounced typing
  useEffect(() => {
    if (debouncedQuery.length === 0) return;
    loadClients(debouncedQuery);
  }, [debouncedQuery, loadClients]);

  // local quick filter (instant)
  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const hay = `${c.name || ""} ${c.email || ""} ${c.company || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [clients, clientQuery]);

  // Keep shipping synced if toggled
  useEffect(() => {
    if (useBillingAsShipping) setShippingAddress({ ...billingAddress });
  }, [useBillingAsShipping, billingAddress]);

  // Close dropdown on outside click (input + portal both excluded)
  useEffect(() => {
    function handleOutside(e) {
      const input = inputAnchorRef.current;
      const menu = portalRef.current;
      if (!clientOpen) return;
      if (input && input.contains(e.target)) return;
      if (menu && menu.contains(e.target)) return;
      setClientOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [clientOpen]);

  // Update portal position when open, on scroll/resize
  useEffect(() => {
    const updatePos = () => {
      const el = inputAnchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPortalBox({ top: r.bottom, left: r.left, width: r.width });
    };
    if (clientOpen) {
      updatePos();
      window.addEventListener("scroll", updatePos, true);
      window.addEventListener("resize", updatePos);
      return () => {
        window.removeEventListener("scroll", updatePos, true);
        window.removeEventListener("resize", updatePos);
      };
    }
  }, [clientOpen]);

  // Keep billing address synced with selected client unless user overrides
  useEffect(() => {
    if (!selectedClient || !keepBillingSynced) return;
    const a = selectedClient.address || {};
    const next = {
      street: a.street || "",
      city: a.city || "",
      state: a.state || "",
      zip: a.zip || "",
      country: a.country || "",
      raw: a.raw || joinAddress(a || {}),
    };
    setBillingAddress(next);
    if (useBillingAsShipping) setShippingAddress(next);
  }, [selectedClient, keepBillingSynced, useBillingAsShipping]);

  /* ------------------------------- handlers ------------------------------- */
  const handleSelectClient = useCallback((c) => {
    setSelectedClientId(c._id);
    setSelectedClient(c);
    setClientQuery(c.name || c.email || "");
    setClientOpen(false);
  }, []);

  const onClientKeyDown = (e) => {
    if (!clientOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setClientOpen(true);
      return;
    }
    if (!clientOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) =>
        Math.min(i + 1, Math.max(0, filteredClients.length - 1))
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = filteredClients[highlightIdx];
      if (c) handleSelectClient(c);
    } else if (e.key === "Escape") {
      setClientOpen(false);
    }
  };

  const addLine = () =>
    setLineItems((prev) => [
      ...prev,
      {
        ...DEFAULT_LINE,
        taxPercent: defaultTaxPct,
        isTaxInclusive: defaultTaxInclusive,
      },
    ]);

  const removeLine = (i) =>
    setLineItems((prev) => prev.filter((_, idx) => idx !== i));

  const duplicateLine = (i) =>
    setLineItems((prev) => {
      const copy = { ...prev[i] };
      return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
    });

  const moveLine = (i, dir) =>
    setLineItems((prev) => {
      const next = [...prev];
      const ni = i + dir;
      if (ni < 0 || ni >= next.length) return prev;
      [next[i], next[ni]] = [next[ni], next[i]];
      return next;
    });

  const patchLine = (i, field, val) =>
    setLineItems((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: val };
      return next;
    });

  const applyDefaultsToAll = () => {
    setLineItems((prev) =>
      prev.map((li) => ({
        ...li,
        taxPercent: defaultTaxPct,
        isTaxInclusive: defaultTaxInclusive,
      }))
    );
    toast.success("Applied defaults to all lines");
  };

  const onBillingChange = (name, value) => {
    if (keepBillingSynced) setKeepBillingSynced(false);
    setBillingAddress((prev) => ({ ...prev, [name]: value }));
  };

  const onShippingChange = (name, value) => {
    setShippingAddress((prev) => ({ ...prev, [name]: value }));
  };

  // ⬇️ Simplified submit: no send-after flow, no invoice-level discount/baseCurrency/exchangeRate
  const submit = async () => {
    const can =
      selectedClientId &&
      lineItems.length > 0 &&
      lineItems.some(
        (li) =>
          Number(li.quantity) > 0 &&
          (Number(li.rate) > 0 || Number(li.discountAmount) > 0)
      ) &&
      !invalidDueDate;

    if (!can) return;
    setSaving(true);
    try {
      const payload = {
        clientId: selectedClientId,
        issueDate: issueDate || undefined,
        dueDate: dueDate || undefined,
        currency: (currency || "USD").toUpperCase(),
        notes,
        lineItems: lineItems.map((li) => ({
          description: li.description || "",
          quantity: Number(li.quantity || 0),
          rate: Number(li.rate || 0),
          unitPrice: Number(li.rate || 0), // ✅ backend compatibility
          taxPercent: clamp(li.taxPercent, 0, 100),
          discountPercent: clamp(li.discountPercent, 0, 100),
          discountAmount: Number(li.discountAmount || 0),
          isTaxInclusive: !!li.isTaxInclusive,
        })),
        billingAddress,
        shippingAddress: useBillingAsShipping
          ? { ...billingAddress }
          : shippingAddress,
        // ❌ invoice-level discount removed
        // ❌ baseCurrency / exchangeRate removed (single-currency for now)
      };

      await api.post("/invoices", payload);
      toast.success("Invoice created");
      navigate("/invoices");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to create invoice";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------ UI ------------------------------ */
  return (
    <div className="min-h-screen bg-slate-50">
      {/* normalize native date widget differences */}
      <style>{`
        input[type="date"]::-webkit-inner-spin-button{display:none;}
        input[type="date"]::-webkit-clear-button{display:none;}
        input[type="date"]::-webkit-calendar-picker-indicator{opacity:0;}
      `}</style>

      {/* Header: clean card (no gradient) */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6">
        <div className="rounded-2xl border border-slate-200 shadow-sm bg-white">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="inline -mt-1 mr-1" size={16} /> Back
                </button>
                <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center">
                  <FileText size={20} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Create Invoice
                  </h1>
                  <p className="text-slate-600 text-sm">
                    Create and send professional invoices.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSave || saving}
                  className={`px-4 py-2 rounded-xl font-semibold shadow-sm border border-slate-200 text-slate-700 hover:bg-slate-50 ${
                    !canSave || saving ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  {saving ? "Saving…" : "Save Draft"}
                </button>
              </div>
            </div>

            {/* Quick Stats (neutral cards to mirror QuoteNew) */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs flex items-center gap-1">
                  <DollarSign size={14} /> Subtotal
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.subtotalBase, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs flex items-center gap-1">
                  <Percent size={14} /> Tax
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.taxTotal, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs">Line discounts</div>
                <div className="text-lg font-bold text-slate-900">
                  -{money(aggregatedDiscountDisplay, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs">Total</div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.totalAmount, currency)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Currency */}
          <Section
            icon={Building2}
            title="Client & Currency"
            hint="Search a client and pick invoice currency"
          >
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-4 items-start">
              <div className="relative">
                <LabeledInput
                  inputRef={inputAnchorRef}
                  name="clientSearch"
                  label="Client"
                  value={clientQuery}
                  onChange={(e) => {
                    // typing clears any prior selection and keeps searching (explicit select only)
                    setClientQuery(e.target.value);
                    setSelectedClientId("");
                    setSelectedClient(null);
                    setClientOpen(true);
                  }}
                  onKeyDown={onClientKeyDown}
                  onFocus={() => setClientOpen(true)}
                  placeholder="Search client by name or email"
                  leftAdornment={<Search size={18} />}
                />

                {/* PORTAL MENU */}
                {clientOpen &&
                  createPortal(
                    <div
                      ref={portalRef}
                      style={{
                        position: "fixed",
                        top: portalBox.top,
                        left: portalBox.left,
                        width: portalBox.width,
                        zIndex: 9999,
                      }}
                      className="mt-1 bg-white border border-slate-200 rounded-xl shadow-xl"
                      role="listbox"
                      aria-activedescendant={`client-opt-${highlightIdx}`}
                    >
                      <div className="max-h-[60vh] overflow-auto overscroll-contain">
                        {filteredClients.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-slate-400">
                            No results
                          </div>
                        ) : (
                          filteredClients.map((c, idx) => {
                            const active = idx === highlightIdx;
                            const selected =
                              String(selectedClientId) === String(c._id);
                            return (
                              <button
                                id={`client-opt-${idx}`}
                                role="option"
                                aria-selected={active}
                                key={c._id}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                                  active
                                    ? "bg-blue-50"
                                    : selected
                                    ? "bg-slate-50"
                                    : ""
                                }`}
                                onMouseEnter={() => setHighlightIdx(idx)}
                                onClick={() => handleSelectClient(c)}
                              >
                                <div className="font-medium text-slate-900">
                                  {c.name || "—"}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {c.email || "—"}{" "}
                                  {c.company ? `• ${c.company}` : ""}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>,
                    document.body
                  )}
              </div>

              <LabeledInput
                name="currency"
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                placeholder="USD"
              />
            </div>

            {selectedClient ? (
              <div className="mt-2 text-[12px] text-slate-600 flex flex-wrap gap-2">
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                  {selectedClient.name}
                </span>
                {selectedClient.email ? (
                  <span className="px-2 py-0.5 rounded-full bg-slate-50 border">
                    {selectedClient.email}
                  </span>
                ) : null}
                {selectedClient.phone ? (
                  <span className="px-2 py-0.5 rounded-full bg-slate-50 border">
                    {selectedClient.phone}
                  </span>
                ) : null}
              </div>
            ) : null}
          </Section>

          {/* Dates */}
          <Section
            icon={CalendarDays}
            title="Dates & Terms"
            hint="Set invoice issue date and due date (quick picks below)"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <LabeledInput
                name="issueDate"
                label="Issue Date"
                value={issueDate}
                onChange={(e) => {
                  setIssueDate(e.target.value);
                  if (!dueDate) setDueDate(plusDaysISO(e.target.value, 30));
                }}
                type="date"
                rightAdornment={<CalendarDays size={16} />}
                onClick={(e) => e.target.showPicker?.()}
              />

              <div>
                <LabeledInput
                  name="dueDate"
                  label="Due Date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  type="date"
                  rightAdornment={<CalendarDays size={16} />}
                  onClick={(e) => e.target.showPicker?.()}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {[0, 7, 15, 30].map((d) => (
                    <button
                      key={d}
                      type="button"
                      className="inline-flex h-9 items-center justify-center rounded-lg border px-3 text-[12px] leading-none hover:bg-slate-50"
                      onClick={() => setDueDate(plusDaysISO(issueDate, d))}
                      aria-label={
                        d === 0
                          ? "Set due date to today"
                          : `Set due date to Net ${d}`
                      }
                    >
                      {d === 0 ? "Today" : `Net ${d}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {invalidDueDate ? (
              <div className="text-[12px] text-rose-600 mt-1">
                Due date must be on or after Issue date.
              </div>
            ) : null}
          </Section>

          {/* Addresses */}
          <Section
            icon={MapPin}
            title="Addresses"
            hint="Prefill from the client's address and edit as needed"
          >
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <LabeledCheckbox
                label="Keep billing synced with client"
                checked={keepBillingSynced}
                onChange={(e) => setKeepBillingSynced(e.target.checked)}
              />
              <LabeledCheckbox
                label="Shipping same as billing"
                checked={useBillingAsShipping}
                onChange={(e) => setUseBillingAsShipping(e.target.checked)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Billing */}
              <div className="space-y-3">
                <div className="text-sm font-semibold text-slate-700">
                  Billing Address
                </div>
                <LabeledInput
                  label="Street"
                  value={billingAddress.street}
                  onChange={(e) => onBillingChange("street", e.target.value)}
                  disabled={keepBillingSynced}
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <LabeledInput
                    label="City"
                    value={billingAddress.city}
                    onChange={(e) => onBillingChange("city", e.target.value)}
                    disabled={keepBillingSynced}
                  />
                  <LabeledInput
                    label="State/Province"
                    value={billingAddress.state}
                    onChange={(e) => onBillingChange("state", e.target.value)}
                    disabled={keepBillingSynced}
                  />
                  <LabeledInput
                    label="ZIP/Postal"
                    value={billingAddress.zip}
                    onChange={(e) => onBillingChange("zip", e.target.value)}
                    disabled={keepBillingSynced}
                  />
                </div>
                <LabeledInput
                  label="Country"
                  value={billingAddress.country}
                  onChange={(e) => onBillingChange("country", e.target.value)}
                  disabled={keepBillingSynced}
                />
                <label className={LABEL_BASE}>Raw (optional)</label>
                <textarea
                  rows={3}
                  className={INPUT_BASE + " h-auto"}
                  value={billingAddress.raw}
                  onChange={(e) => onBillingChange("raw", e.target.value)}
                  placeholder="Street, City, State, ZIP, Country"
                  disabled={keepBillingSynced}
                />
              </div>

              {/* Shipping */}
              <div className="space-y-3">
                <div className="text-sm font-semibold text-slate-700">
                  Shipping Address
                </div>
                <LabeledInput
                  label="Street"
                  value={
                    useBillingAsShipping
                      ? billingAddress.street
                      : shippingAddress.street
                  }
                  onChange={(e) => onShippingChange("street", e.target.value)}
                  disabled={useBillingAsShipping}
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <LabeledInput
                    label="City"
                    value={
                      useBillingAsShipping
                        ? billingAddress.city
                        : shippingAddress.city
                    }
                    onChange={(e) => onShippingChange("city", e.target.value)}
                    disabled={useBillingAsShipping}
                  />
                  <LabeledInput
                    label="State/Province"
                    value={
                      useBillingAsShipping
                        ? billingAddress.state
                        : shippingAddress.state
                    }
                    onChange={(e) => onShippingChange("state", e.target.value)}
                    disabled={useBillingAsShipping}
                  />
                  <LabeledInput
                    label="ZIP/Postal"
                    value={
                      useBillingAsShipping
                        ? billingAddress.zip
                        : shippingAddress.zip
                    }
                    onChange={(e) => onShippingChange("zip", e.target.value)}
                    disabled={useBillingAsShipping}
                  />
                </div>
                <LabeledInput
                  label="Country"
                  value={
                    useBillingAsShipping
                      ? billingAddress.country
                      : shippingAddress.country
                  }
                  onChange={(e) => onShippingChange("country", e.target.value)}
                  disabled={useBillingAsShipping}
                />
                <label className={LABEL_BASE}>Raw (optional)</label>
                <textarea
                  rows={3}
                  className={INPUT_BASE + " h-auto"}
                  value={
                    useBillingAsShipping
                      ? billingAddress.raw
                      : shippingAddress.raw
                  }
                  onChange={(e) => onShippingChange("raw", e.target.value)}
                  placeholder="Street, City, State, ZIP, Country"
                  disabled={useBillingAsShipping}
                />
              </div>
            </div>
          </Section>

          {/* Line Items */}
          <Section
            icon={DollarSign}
            title="Items & Services"
            hint="Discount amount overrides percent; 'Tax inclusive' backs out tax from rate."
          >
            {/* Defaults Toolbar */}
            <div className="flex flex-wrap gap-3 items-end">
              <div className="w-28">
                <LabeledInput
                  name="defaultTax"
                  label="Default Tax %"
                  type="number"
                  value={defaultTaxPct}
                  onChange={(e) => setDefaultTaxPct(Number(e.target.value))}
                />
              </div>
              <div className="pt-6">
                <LabeledCheckbox
                  label="Tax inclusive"
                  checked={defaultTaxInclusive}
                  onChange={(e) => setDefaultTaxInclusive(e.target.checked)}
                />
              </div>
              <button
                type="button"
                className="ml-auto px-3.5 py-2.5 rounded-xl border text-sm hover:bg-slate-50"
                onClick={applyDefaultsToAll}
              >
                Apply defaults to all
              </button>
            </div>

            {/* Cards */}
            <div className="space-y-4 mt-3">
              {lineItems.map((li, i) => {
                const c = calcLine(li);
                const hasDiscount =
                  Number(li.discountAmount) > 0 ||
                  Number(li.discountPercent) > 0;

                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold text-slate-700">
                        Item {i + 1}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-slate-50"
                          title="Move up"
                          onClick={() => moveLine(i, -1)}
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-slate-50"
                          title="Move down"
                          onClick={() => moveLine(i, 1)}
                        >
                          <ArrowDown size={16} />
                        </button>
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-slate-50"
                          title="Duplicate"
                          onClick={() => duplicateLine(i)}
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          type="button"
                          className="p-2 rounded-xl border hover:bg-rose-50 text-rose-600"
                          title="Remove"
                          onClick={() => removeLine(i)}
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <LabeledInput
                        label="Qty"
                        type="number"
                        value={li.quantity}
                        onChange={(e) =>
                          patchLine(i, "quantity", Number(e.target.value))
                        }
                      />
                      <LabeledInput
                        label="Rate"
                        type="number"
                        value={li.rate}
                        onChange={(e) =>
                          patchLine(i, "rate", Number(e.target.value))
                        }
                        rightAdornment={<DollarSign size={14} />}
                      />

                      {/* Discount block: collapsible, spans 2 columns */}
                      <div className="col-span-2">
                        {li.showDiscount ? (
                          <div className="grid grid-cols-2 gap-3">
                            <LabeledInput
                              label="Disc %"
                              type="number"
                              value={li.discountPercent}
                              onChange={(e) =>
                                patchLine(
                                  i,
                                  "discountPercent",
                                  Number(e.target.value)
                                )
                              }
                              rightAdornment={<Percent size={14} />}
                            />
                            <LabeledInput
                              label="Disc $"
                              type="number"
                              value={li.discountAmount}
                              onChange={(e) =>
                                patchLine(
                                  i,
                                  "discountAmount",
                                  Number(e.target.value)
                                )
                              }
                              rightAdornment={<DollarSign size={14} />}
                            />
                            <div className="col-span-2">
                              <button
                                type="button"
                                className="text-[12px] underline text-slate-600 hover:text-slate-800"
                                onClick={() =>
                                  patchLine(i, "showDiscount", false)
                                }
                              >
                                Hide discount
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 h-11">
                            <button
                              type="button"
                              className="px-3.5 py-2 rounded-lg border text-sm hover:bg-slate-50"
                              onClick={() =>
                                patchLine(i, "showDiscount", true)
                              }
                            >
                              Add discount
                            </button>
                            {hasDiscount && (
                              <span
                                className="text-[12px] px-2 py-1 rounded-full border bg-slate-50 text-slate-700"
                                title="Click to edit"
                                onClick={() =>
                                  patchLine(i, "showDiscount", true)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                −
                                {Number(li.discountAmount) > 0
                                  ? money(li.discountAmount, currency)
                                  : `${li.discountPercent}%`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <LabeledInput
                        label="Tax %"
                        type="number"
                        value={li.taxPercent}
                        onChange={(e) =>
                          patchLine(i, "taxPercent", Number(e.target.value))
                        }
                        rightAdornment={<Percent size={14} />}
                      />
                      <div className="pt-7">
                        <LabeledCheckbox
                          label="Tax incl."
                          checked={!!li.isTaxInclusive}
                          onChange={(e) =>
                            patchLine(
                              i,
                              "isTaxInclusive",
                              e.target.checked
                            )
                          }
                        />
                      </div>
                    </div>

                    {/* Description + totals */}
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
                      <div className="lg:col-span-6">
                        <label className={LABEL_BASE}>Description</label>
                        <textarea
                          rows={2}
                          className={INPUT_BASE + " h-auto"}
                          placeholder="Describe the product or service"
                          value={li.description}
                          onChange={(e) =>
                            patchLine(i, "description", e.target.value)
                          }
                        />
                      </div>

                      <div className="lg:col-span-6 grid grid-cols-3 md:grid-cols-6 gap-3 text-[13px]">
                        <div className="col-span-1">
                          <div className="text-slate-500">Subtotal</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.subtotalBase, currency)}
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="text-slate-500">Tax</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.taxAmount, currency)}
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-2">
                          <div className="text-slate-500">Line Total</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.total, currency)}
                          </div>
                        </div>
                        <div className="col-span-3 md:col-span-2 flex items-end justify-end">
                          <div className={HINT_BASE}>
                            Amount first, then % (if both set, amount wins).
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center pt-1">
              <div className={HINT_BASE + " flex items-center gap-1"}>
                <Info size={14} />
                Use ↑/↓ to re-order. Totals update instantly.
              </div>
              <button
                type="button"
                className="px-3.5 py-2.5 rounded-xl border text-sm hover:bg-slate-50"
                onClick={addLine}
              >
                <Plus className="inline -mt-1 mr-1" size={16} />
                Add another line
              </button>
            </div>
          </Section>

          {/* Notes */}
          <Section
            icon={FileText}
            title="Notes"
            hint="Shown on the invoice PDF & emails"
          >
            <textarea
              rows={3}
              className={INPUT_BASE + " h-auto"}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional message / terms / thank you note"
            />
          </Section>

          {/* 🔻 Advanced section removed (no invoice-level discount, no multi-currency UI) */}
        </div>

        {/* Right column: sticky summary */}
        <div className="space-y-6">
          <div className="sticky top-6">
            <Section
              icon={DollarSign}
              title="Summary"
              hint="Review totals before creating"
            >
              <div className="space-y-2 text-[15px]">
                <div className="flex justify-between">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-semibold tabular-nums">
                    {money(totals.subtotalBase, currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Line discounts</span>
                  <span className="font-semibold tabular-nums">
                    -{money(aggregatedDiscountDisplay, currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tax</span>
                  <span className="font-semibold tabular-nums">
                    {money(totals.taxTotal, currency)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-2" />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold text-slate-800">Total</span>
                  <span className="font-extrabold tabular-nums">
                    {money(totals.totalAmount, currency)}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2">
                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={submit}
                  className={`px-4 py-2 rounded-xl font-semibold text-white shadow-sm ${
                    !canSave || saving
                      ? "bg-slate-400"
                      : "bg-slate-900 hover:bg-black"
                  }`}
                >
                  {saving ? "Saving…" : "Create Invoice"}
                </button>

                <div className="text-[12px] text-slate-500 flex items-center gap-2 mt-2">
                  <Info size={14} className="text-slate-400" />
                  Rounding: HALF_UP • Due date must be ≥ issue date
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
